// @flow
import React, { PureComponent, Fragment } from 'react';
import type { Node } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import styles from 'components/DealerInfo.scss';
import classNames from 'classnames';

import Icon from 'Common/Icon';
import Button from 'Common/Button';
import Image from 'Common/Image';
import { connect } from 'react-redux';
import { distanceOrTown } from 'Common/distanceOrTown';
import { valueOrNA } from 'Common/valueOrNA';
import { toCapitalize } from 'Common/string';
import formatPhoneNumber from 'Common/formatPhoneNumber';
import { gtmEventPush } from 'utils/gtm/events';
import isWorkingHours from 'utils/isWorkingHours';

/**
 * <DealerInfo /> component.
 */
export class DealerInfo extends PureComponent<*, *> {
  /**
   * Implements propTypes().
   *
   * @return {Object} - propType object.
   */
  static propTypes = {
    showButton: PropTypes.bool,
    showStrapLine: PropTypes.bool,
    showDealerWebsite: PropTypes.bool,
    pageLayout: PropTypes.string,
    extendStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    data: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      phone: PropTypes.fl,
      distanceOrTown: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      email: PropTypes.string,
      logo: PropTypes.string,
      strapline: PropTypes.string
    }),
    targetScroll: PropTypes.object,
    colorIcons: PropTypes.string,
    item: PropTypes.object,
    buttonOnClick: PropTypes.func,
    changeFormTypeFunc: PropTypes.func,
    gtmSectionName: PropTypes.string
  };

  /**
   *    Default Props implementation.
   */
  static defaultProps: Object = {
    showButton: true,
    showStrapLine: false,
    colorIcons: 'dark',
    showDealerWebsite: false
  };

  constructor(props: Object) {
    super(props);
    this.state = { decodedPhoneNo: '' };
  }

  /**
   * MapStateToProps implementation.
   * @return {object} new state object
   * @param {object} state object
   */
  static mapStateToProps(state: Object): Object {
    return {
      item: state.listing,
      isMobile: state.viewport.isXs
    };
  }

  revealPhoneNoClick: (e: SyntheticEvent<HTMLButtonElement>) => void = e => {
    e.preventDefault();

    const { item } = this.props;

    const encodedNoOrNA = valueOrNA(item.dealer, 'encodedNo');
    const decodedPhoneNo =
      encodedNoOrNA === 'N/A' ? encodedNoOrNA : window.atob(encodedNoOrNA);

    this.setState({
      decodedPhoneNo
    });

    gtmEventPush({
      method: 'phoneNoReveal',
      args: {
        actionPrefix: this.props.gtmSectionName,
        sourceId: this.props.item.sourceId
      }
    });
  };

  pushGtmDealerClick: (e: SyntheticEvent<HTMLButtonElement>) => void = () => {
    const { item } = this.props;
    const id = valueOrNA(item.dealer, 'id');
    gtmEventPush({
      method: 'moreStockDealer',
      args: {
        actionPrefix: this.props.gtmSectionName,
        dealerId: id
      }
    });
  };

  pushGtmPhoneNoClick: (e: SyntheticEvent<HTMLButtonElement>) => void = () => {
    gtmEventPush({
      method: 'phoneNoClick',
      args: {
        actionPrefix: this.props.gtmSectionName,
        sourceId: this.props.item.sourceId
      }
    });
  };

  handleEmailDealerButton: (
    e: SyntheticEvent<HTMLButtonElement>
  ) => void = e => {
    // Stop the parent onClick from happening too.
    e.stopPropagation();

    gtmEventPush({
      method: 'ctaEmailDealer',
      args: {
        actionPrefix: this.props.gtmSectionName,
        sourceId: this.props.item.sourceId
      }
    });

    this.props.buttonOnClick('message');
  };

  handleRequestCallbackButton: (
    e: SyntheticEvent<HTMLButtonElement>
  ) => void = e => {
    // Stop the parent onClick from happening too.
    e.stopPropagation();

    gtmEventPush({
      method: 'ctaRequestCallback',
      args: {
        actionPrefix: this.props.gtmSectionName,
        sourceId: this.props.item.sourceId
      }
    });

    this.props.buttonOnClick('callback');
  };

  renderPhone(): Node {
    const { decodedPhoneNo } = this.state;

    return !decodedPhoneNo ? (
      <a
        title={'Dealer Phone Number Reveal'}
        onClick={this.revealPhoneNoClick}
        className={styles.classDealersClickToReveal}
      >
        Reveal number
      </a>
    ) : (
      <a
        href={`tel:${decodedPhoneNo}`}
        title={'Dealer Phone Number'}
        onClick={this.pushGtmPhoneNoClick}
        className={styles.classDealersPhoneRevealed}
      >
        {formatPhoneNumber(decodedPhoneNo)}
      </a>
    );
  }

  /**
   * Renders <DealerInfo /> component.
   *
   * @return {JSX} - renders DealerInfo.
   */
  render(): Node {
    const { item, colorIcons, isMobile } = this.props;
    const name = valueOrNA(item.dealer, 'name');
    const distanceTown = distanceOrTown(item);
    const logo = valueOrNA(item.dealer, 'logo');
    const id = valueOrNA(item.dealer, 'id');
    const strapline =
      'At What Car? we’ve got more than 45 years of experience of helping people buy the best car for their budget. Every car sold by our exclusive used car partner Heycar comes with a warranty, and is guaranteed to be less than eight years and 100,000 miles old.';

    const {
      classDealerInfo,
      classDealerInfoPhone,
      classDealerInfoLocation,
      classDealerImage,
      classDealerMoreDeals,
      classDealerCTA,
      classDealerInfoStrapLine,
      classDealerInfoLightIcons
    } = styles;

    return (
      <div
        className={classNames(
          this.props.extendStyle,
          classDealerInfo,
          colorIcons !== 'light' || classDealerInfoLightIcons
        )}
      >
        <h4>{name}</h4>
        <div className={classNames(classDealerInfoLocation)}>
          <Icon
            iconName={'icomoon-38'}
            size={'small'}
            className={styles.icon}
          />
          {typeof distanceTown === 'string'
            ? toCapitalize(distanceTown)
            : distanceTown}
        </div>
        {isWorkingHours() && (
          <div className={classNames(classDealerInfoPhone)}>
            <Icon iconName={'phone2'} size={'small'} className={styles.icon} />
            {this.renderPhone()}
          </div>
        )}
        <Link
          to={`/used-cars/dealer/${id}`}
          className={classDealerMoreDeals}
          onClick={this.pushGtmDealerClick}
        >
          More stock from dealer
        </Link>

        {this.props.showButton && (
          <Fragment>
            <div className={classDealerCTA}>
              {!isMobile && (
                <Button
                  extendStyle={classDealerCTA}
                  buttonText={'Message dealer'}
                  item={this.props.item}
                  gaLabel="heycar-message-dealer-button"
                  onClick={this.handleEmailDealerButton}
                />
              )}
            </div>
            <div className={classDealerCTA}>
              {!isWorkingHours() && !isMobile && (
                <Button
                  extendStyle={classDealerCTA}
                  buttonText={'Request callback'}
                  item={this.props.item}
                  gaLabel="heycar-callback-dealer-button"
                  onClick={this.handleRequestCallbackButton}
                  type={'transparent'}
                />
              )}
            </div>
          </Fragment>
        )}
        {logo && logo !== 'N/A' && (
          <div className={classDealerCTA}>
            <Image
              src={logo}
              alt={name}
              title={name}
              className={classDealerImage}
              onErrorEvent={'delete'}
            />
          </div>
        )}
        {this.props.showStrapLine && (
          <p className={classDealerInfoStrapLine}>{strapline}</p>
        )}
      </div>
    );
  }
}

// $FlowFixMe
export default connect(DealerInfo.mapStateToProps, null)(DealerInfo);



// WEBPACK FOOTER //
// ./src/Common/DealerInfo/index.js